/**
 * Custom Fields — client API (CF01/CF12) — CL01.
 *
 * Segue la convenzione degli altri client di `src/api`: un'istanza Axios con la
 * base del backend statistiche più il proprio prefisso, e gli interceptor JWT,
 * lingua ed errori già esistenti.
 *
 * **Non concatenare `/custom-fields` a `statisticsApi`**: la sua base termina
 * già con `/statistics`, e il dominio Custom Fields vive accanto, non dentro.
 *
 * Due cose che questo file espone perché il chiamante non le scopra per caso:
 *
 * - **`saveSchema` risponde 202.** Significa che l'*intento* è stato validato,
 *   non che lo schema è memorizzato: il motore completa la scrittura con il
 *   salvataggio del modello. Per il client un 2xx è però un successo, e basta:
 *   lo stato `applied` lo scrive solo il ponte motore, che può non essere
 *   attivo, e attenderlo trasformava un salvataggio riuscito in un errore.
 * - **i conflitti attesi non producono snackbar.** `raise: false` è la
 *   convenzione già in uso: l'errore raggiunge il chiamante, che decide come
 *   mostrarlo. Un 409 di duplicato è una risposta prevista, non un guasto.
 */

import axios from "axios";
import jwtInterceptor from "./jwtInterceptor";
import errorInterceptor from "./errorInterceptor";
import type { AuditPage, DataEnvelope, FieldDefinition, FieldError, FormulaPreviewResult, ImpactResult, OperationStatus, Page, RegistryMutation, RegistryPreview, RegistryRead, Revision, RuntimeContext, SchemaDocument, ValuesPatch } from "model/CustomFields";

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/custom-fields`,
  headers: {
    "Content-Type": "application/json",
  },
  // Il default di axios e' "aspetta per sempre". Una rotta che non risponde —
  // un servizio non ancora esposto, un proxy che inghiotte la richiesta —
  // lascerebbe ogni attesa in caricamento **indefinitamente**: l'utente vede
  // uno spinner e nessun errore, e non ha modo di sapere che qualcosa e'
  // rotto. Un limite di tempo trasforma il silenzio in un errore mostrabile.
  //
  // Generoso di proposito: il servizio e' remoto, e una risposta lenta non deve
  // diventare un errore.
  timeout: 20000,
});

instance.interceptors.request.use(jwtInterceptor);
instance.interceptors.response.use(undefined, errorInterceptor);

/**
 * Una richiesta in cui un rifiuto è una risposta prevista.
 *
 * `raise: false` impedisce all'interceptor di mostrare uno snackbar: un 409 di
 * duplicato o un 422 di validazione vengono gestiti dal form, che sa dove
 * mettere il messaggio. Uno snackbar globale per un errore già mostrato accanto
 * al campo è la doppia notifica che il piano vieta.
 */
const expectedRefusal = { params: { raise: false } } as const;

/** Il corpo d'errore del contratto: `{error:{code,messageKey,params,fieldErrors}}`. */
export interface ApiErrorBody {
  code: string;
  messageKey?: string;
  params?: Record<string, unknown>;
  /**
   * I campi che il server ha rifiutato, quando il rifiuto riguarda dei campi.
   *
   * L'envelope del contratto li porta sempre, e il commento qui sopra lo
   * diceva già; questa dichiarazione però non li elencava, quindi **nessuno li
   * leggeva**: il rifiuto di uno schema arrivava al progettista come «correggi i
   * valori indicati» senza una riga indicata.
   */
  fieldErrors?: FieldError[];
}

/**
 * Il rifiuto, letto com'e' fatto.
 *
 * L'envelope d'errore del contratto e' `{error:{code,...},requestId}`: `error`
 * e' un **oggetto**, non una stringa. Trattarlo come stringa produce
 * `"[object Object]"`, e nessun confronto con un codice — `=== "DUPLICATE_
 * CONFIRMATION_REQUIRED"` compreso — scatta mai: il ramo esiste e non entra.
 *
 * Tollerante sul resto perché non tutti i percorsi sono del contratto: il
 * motore legacy risponde a modo suo, e un errore che non si riesce a leggere
 * deve restare un rifiuto generico, non un'eccezione nel gestore dell'errore.
 */
export function apiError(error: unknown): ApiErrorBody | null {
  const corpo = (error as { response?: { data?: unknown } })?.response?.data as
    | Record<string, unknown>
    | undefined;
  if (!corpo || typeof corpo !== "object") return null;

  const dentro = (corpo.error ?? (corpo.data as Record<string, unknown>)?.error) as unknown;
  if (dentro && typeof dentro === "object" && typeof (dentro as ApiErrorBody).code === "string") {
    return dentro as ApiErrorBody;
  }
  // Forme piatte: il solo codice, o `error` come stringa.
  const piatto = typeof corpo.code === "string" ? corpo.code : corpo.error;
  return typeof piatto === "string" ? { code: piatto } : null;
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const customFieldsApi = {
  /** Schema normalizzato e revisioni. Un modello non migrato viene proiettato. */
  async getSchema(modelId: string): Promise<SchemaDocument> {
    const { data } = await instance.get<DataEnvelope<SchemaDocument>>(
      `/models/${modelId}/schema`
    );
    return data.data;
  },

  /** Analisi degli impatti. Non memorizza lo schema che analizza. */
  async analyzeSchema(
    modelId: string,
    input: {
      operationId: string;
      expectedRevision: { schema: number; policy: number };
      schema: SchemaDocument;
      reconciliation: "next_access" | "next_save_or_transition";
      impactToken?: string;
      confirmationToken?: string;
    }
  ): Promise<ImpactResult> {
    const { data } = await instance.post<DataEnvelope<ImpactResult>>(
      `/models/${modelId}/impact`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /**
   * Memorizza l'intento dello schema.
   *
   * Il `status` che torna è `validated`, e HTTP 202 lo dice: nulla è stato
   * applicato. Il chiamante deve inoltrare lo stesso `operationId` e lo stesso
   * schema al salvataggio del modello, che è dove la scrittura avviene davvero.
   */
  async saveSchema(
    modelId: string,
    input: {
      operationId: string;
      expectedRevision: { schema: number; policy: number };
      schema: SchemaDocument;
      reconciliation: "next_access" | "next_save_or_transition";
      impactToken?: string;
      confirmationToken?: string;
    }
  ): Promise<{ operationId: string; status: "validated"; schema: SchemaDocument }> {
    const { data } = await instance.put<
      DataEnvelope<{ operationId: string; status: "validated"; schema: SchemaDocument }>
    >(`/models/${modelId}/schema`, input, expectedRefusal);
    return data.data;
  },

  /**
   * Anteprima su campioni espliciti: non legge valori reali nascosti.
   *
   * Il tipo del risultato non si manda: lo legge il server dalla formula e lo
   * restituisce (`outputType`), perche' una dichiarazione scritta accanto a una
   * formula puo' contraddirla — e a salvarla sarebbe la dichiarazione.
   */
  async previewFormula(
    modelId: string,
    input: {
      expression: string;
      precision: number;
      samples: Record<string, string | boolean | string[] | null>;
      additionalFields?: readonly FieldDefinition[];
    }
  ): Promise<FormulaPreviewResult> {
    const { data } = await instance.post<DataEnvelope<FormulaPreviewResult>>(
      `/models/${modelId}/formula-preview`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  // -------------------------------------------------------------------------
  // Runtime
  // -------------------------------------------------------------------------

  /**
   * Contesto prima che il processo esista.
   *
   * Non richiede il permesso designer: un operatore semplice deve poter aprire
   * il form di avvio senza ricevere la configurazione intera del modello. Non
   * prenota nulla — un progressivo qui è `null`, non un numero già assegnato.
   *
   * `changes` è il testo grezzo che l'utente ha digitato: si rimanda perché il
   * server ricalcoli i calcolati contro di esso (il client non valuta mai una
   * formula). Un rifiuto è previsto — mentre si scrive un numero può non essere
   * ancora valido — e non deve produrre uno snackbar: il chiamante decide se ha
   * senso mostrarlo.
   */
  async getStartContext(
    modelId: string,
    input: {
      operationId: string;
      instanceHandle: string;
      changes?: Record<string, string | boolean | string[] | null>;
    }
  ): Promise<RuntimeContext> {
    const { data } = await instance.post<DataEnvelope<RuntimeContext>>(
      `/models/${modelId}/start-context`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /**
   * Schema, valori e accesso autorizzati.
   *
   * Omettere `contextId` acquisisce un contesto nuovo, ed è ciò che applica un
   * checkpoint `next_access`. Nominarne uno mantiene la sessione sulle regole
   * che le sono state date.
   */
  async getContext(
    processId: string,
    options: { taskId?: string; contextId?: string } = {}
  ): Promise<RuntimeContext> {
    const { data } = await instance.get<DataEnvelope<RuntimeContext>>(
      `/processes/${processId}/context`,
      { params: { taskId: options.taskId, contextId: options.contextId } }
    );
    return data.data;
  },

  /**
   * Scrittura differenziale: solo i campi davvero cambiati.
   *
   * Rimandare indietro la mappa intera sovrascriverebbe i valori che il
   * chiamante non può vedere con il sottoinsieme che vede. `buildChanges` in
   * `components/custom-fields/legacyAdapter` costruisce la patch corretta.
   */
  async patchValues(
    processId: string,
    input: {
      operationId: string;
      contextId: string;
      expectedRevision: Revision;
      changes: Record<string, string | boolean | string[] | null>;
    }
  ): Promise<RuntimeContext> {
    const { data } = await instance.patch<DataEnvelope<RuntimeContext>>(
      `/processes/${processId}/values`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  // -------------------------------------------------------------------------
  // Operazioni
  // -------------------------------------------------------------------------

  /**
   * L'esito di un'operazione, da interrogare dopo un timeout.
   *
   * È l'endpoint che rende sicuro un retry: dice cosa l'operazione ha davvero
   * fatto, invece di rieseguirla. Non porta mai il ticket, che è un segreto del
   * motore.
   */
  async getOperation(operationId: string): Promise<{
    operationId: string;
    status: OperationStatus;
    expiresAt?: string | null;
    resultPreview?: Record<string, string | boolean | string[] | null> | null;
  }> {
    const { data } = await instance.get<
      DataEnvelope<{
        operationId: string;
        status: OperationStatus;
        expiresAt?: string | null;
        resultPreview?: Record<string, string | boolean | string[] | null> | null;
      }>
    >(`/operations/${operationId}`, expectedRefusal);
    return data.data;
  },

  /**
   * Conferma umana di un insieme di anomalie.
   *
   * Il consenso è legato alla situazione analizzata: se il candidato o le
   * collisioni si muovono, la conferma è stale e serve una nuova analisi,
   * invece di stirare il vecchio consenso su una situazione diversa.
   */
  async confirmOperation(
    operationId: string,
    input: { challengeId: string; confirmationToken: string }
  ): Promise<{
    operationId: string;
    status: OperationStatus;
    resultPreview?: Record<string, string | boolean | string[] | null> | null;
  }> {
    const { data } = await instance.post<
      DataEnvelope<{
        operationId: string;
        status: OperationStatus;
        resultPreview?: Record<string, string | boolean | string[] | null> | null;
      }>
    >(`/operations/${operationId}/confirm`, input, expectedRefusal);
    return data.data;
  },

  // -------------------------------------------------------------------------
  // Registri
  // -------------------------------------------------------------------------

  async getRegistries(
    organizationId: string,
    options: { cursor?: string; limit?: number } = {}
  ): Promise<Page<RegistryRead>> {
    const { data } = await instance.get<DataEnvelope<Page<RegistryRead>>>(
      `/organizations/${organizationId}/registries`,
      { params: options }
    );
    return data.data;
  },

  /**
   * Crea un registro. La revisione attesa e' 0: un registro che non esiste non
   * ha una revisione da difendere.
   */
  async createRegistry(
    organizationId: string,
    input: RegistryMutation
  ): Promise<RegistryRead> {
    const { data } = await instance.post<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /**
   * Aggiorna un registro.
   *
   * `code` non si manda: cambiare l'identita' di un registro romperebbe i
   * codici che ha gia' emesso, e il contratto lo ignora in modifica.
   */
  async updateRegistry(
    organizationId: string,
    registryId: string,
    input: RegistryMutation
  ): Promise<RegistryRead> {
    const { data } = await instance.patch<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /**
   * Archivia un registro: soft delete.
   *
   * Il progresivo esce dall'elenco ma resta con i codici che ha gia' emesso.
   * Stesso envelope delle altre rotte registro: la risposta e' il registro
   * archiviato, non un esito vuoto.
   */
  async archiveRegistry(
    organizationId: string,
    registryId: string,
    input: { operationId: string; expectedRevision: number; reason?: string }
  ): Promise<RegistryRead> {
    const { data } = await instance.post<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}/archive`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /**
   * Cancella un registro: hard delete.
   *
   * La riga sparisce e il codice torna libero (un progressivo locale ricreato
   * con lo stesso nome non collide). Il server rifiuta se ha già emesso.
   */
  async deleteRegistry(
    organizationId: string,
    registryId: string,
    input: { operationId: string; expectedRevision: number; reason?: string }
  ): Promise<RegistryRead> {
    const { data } = await instance.post<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}/delete`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /** Un registro: configurazione, revisione di allocazione e cursore. */
  async getRegistry(organizationId: string, registryId: string): Promise<RegistryRead> {
    const { data } = await instance.get<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}`
    );
    return data.data;
  },

  /** Anteprima indicativa: non prenota nulla e non promette un numero preciso. */
  async previewRegistry(
    organizationId: string,
    registryId: string,
    input: { operationId: string; expectedRevision: number }
  ): Promise<RegistryPreview> {
    const { data } = await instance.post<DataEnvelope<RegistryPreview>>(
      `/organizations/${organizationId}/registries/${registryId}/preview`,
      input
    );
    return data.data;
  },

  /** Salti, arretramenti e collisioni possibili di una modifica amministrativa. */
  async analyzeRegistry(
    organizationId: string,
    registryId: string,
    input: RegistryMutation
  ): Promise<ImpactResult> {
    const { data } = await instance.post<DataEnvelope<ImpactResult>>(
      `/organizations/${organizationId}/registries/${registryId}/impact`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /** Azzera il progressivo. La parola è confrontata letteralmente. */
  async resetRegistry(
    organizationId: string,
    registryId: string,
    input: RegistryMutation & { keyword: string; reason: string }
  ): Promise<RegistryRead> {
    const { data } = await instance.post<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}/reset`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /** Sposta il cursore, conservando l'ultima emissione reale nello storico. */
  async overrideRegistry(
    organizationId: string,
    registryId: string,
    input: RegistryMutation & { lastIssued: number; reason: string }
  ): Promise<RegistryRead> {
    const { data } = await instance.post<DataEnvelope<RegistryRead>>(
      `/organizations/${organizationId}/registries/${registryId}/override`,
      input,
      expectedRefusal
    );
    return data.data;
  },

  /** Storico sola lettura, il più recente per primo. */
  async getRegistryAudit(
    organizationId: string,
    registryId: string,
    options: { cursor?: string; limit?: number } = {}
  ): Promise<AuditPage> {
    const { data } = await instance.get<DataEnvelope<AuditPage>>(
      `/organizations/${organizationId}/registries/${registryId}/audit`,
      { params: options }
    );
    return data.data;
  },
};

export default customFieldsApi;
